import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Redirect } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCalendarPlus,
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaPlus,
  FaSyncAlt,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import BackButton from "../ChildComponent/BackButton";
import SessionManager from "../Utils/SessionManager";
import MdlSelectSession from "../dialog/MdlSelectSession";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  '"Segoe UI", system-ui, -apple-system, Roboto, "Helvetica Neue", Arial, sans-serif';

const HOLIDAY_TYPES = [
  "National",
  "Gazetted",
  "Festival",
  "Local",
  "School",
  "Restricted",
  "Vacation",
  "Other",
];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function statusTone(status) {
  const s = String(status || "")
    .trim()
    .toLowerCase();
  if (s === "over" || s.includes("over") || s.includes("past")) return "over";
  if (s === "active" || s.includes("upcoming") || s.includes("current")) {
    return "active";
  }
  return "neutral";
}

function formatDateLabel(value) {
  if (!value) return "—";
  const raw = String(value).trim();
  if (!raw) return "—";
  // Keep API format if already human-readable; trim time if datetime.
  if (raw.includes(" ")) return raw.split(" ")[0];
  return raw;
}

function festivalDateKey(value) {
  return String(value || "").trim().slice(0, 10);
}

function isPublicHoliday(festival) {
  return (
    festival?.is_public_holiday === 1 ||
    festival?.is_public_holiday === "1" ||
    festival?.is_public_holiday === true
  );
}

function parseYmd(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const iso = raw.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const dmy = raw.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  return iso;
}

function eachYmdInRange(start, end, visit) {
  const s = parseYmd(start);
  const e = parseYmd(end) || s;
  if (!s) return;
  const cur = new Date(`${s}T00:00:00`);
  const last = new Date(`${e}T00:00:00`);
  if (Number.isNaN(cur.getTime()) || Number.isNaN(last.getTime())) {
    visit(s);
    return;
  }
  // Safety: cap long ranges
  let guard = 0;
  while (cur <= last && guard < 400) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, "0");
    const d = String(cur.getDate()).padStart(2, "0");
    visit(`${y}-${m}-${d}`);
    cur.setDate(cur.getDate() + 1);
    guard += 1;
  }
}

//Created By Cursor
const HolidayCard = memo(function HolidayCard({
  item,
  index,
  busy,
  onEdit,
  onDelete,
}) {
  const tone = statusTone(item.status);
  return (
    <article className="hl-card" style={{ "--hl-i": index }}>
      <div className="hl-card__head">
        <div className="min-w-0">
          <h3 className="hl-card__title" title={item.holiday_Occasion || ""}>
            {item.holiday_Occasion || "—"}
          </h3>
          <div className="hl-card__chips">
            <span className={`hl-status hl-status--${tone}`}>
              {item.status || "—"}
            </span>
            {item.holiday_type ? (
              <span className="hl-chip">{item.holiday_type}</span>
            ) : null}
          </div>
        </div>
        <div className="hl-card__actions">
          <button
            type="button"
            className="hl-icon-btn hl-icon-btn--edit"
            title="Edit holiday"
            disabled={busy}
            onClick={() => onEdit(item)}
          >
            <FaEdit />
          </button>
          <button
            type="button"
            className="hl-icon-btn hl-icon-btn--delete"
            title="Delete holiday"
            disabled={busy}
            onClick={() => onDelete(item)}
          >
            <FaTrash />
          </button>
        </div>
      </div>

      <div className="hl-card__meta">
        <div className="hl-meta">
          <span className="hl-meta__label">From</span>
          <strong>{formatDateLabel(item.start_holiday_date)}</strong>
        </div>
        <div className="hl-meta">
          <span className="hl-meta__label">To</span>
          <strong>{formatDateLabel(item.end_holiday_date)}</strong>
        </div>
        <div className="hl-meta">
          <span className="hl-meta__label">Announced</span>
          <strong>{formatDateLabel(item.date_added)}</strong>
        </div>
      </div>

      <p className="hl-card__remark">
        {item.holiday_remark ? item.holiday_remark : "No remark"}
      </p>
    </article>
  );
});

//Created By Cursor
const FestivalRow = memo(function FestivalRow({
  festival,
  alreadyAdded,
  addedOccasion,
  busy,
  addingId,
  onAdd,
}) {
  const date = parseYmd(festival.festival_date) || festivalDateKey(festival.festival_date);
  const isPublic = isPublicHoliday(festival);
  const isAdding = String(addingId) === String(festival.festival_id);

  return (
    <article className={`hl-fest-card${alreadyAdded ? " hl-fest-card--added" : ""}`}>
      <div className="hl-fest-card__main">
        <h3 className="hl-fest-card__title" title={festival.festival_name || ""}>
          {festival.festival_name || "—"}
        </h3>
        <div className="hl-fest-card__meta">
          <span className="hl-fest-card__date">{formatDateLabel(date)}</span>
          {isPublic ? (
            <span className="hl-chip hl-chip--gazetted">Gazetted</span>
          ) : (
            <span className="hl-chip">Festival</span>
          )}
          {festival.type ? <span className="hl-chip">{festival.type}</span> : null}
        </div>
        {festival.short_desc ? (
          <p className="hl-fest-card__desc">{festival.short_desc}</p>
        ) : null}
        {alreadyAdded ? (
          <p className="hl-fest-card__added-note">
            On this date holiday is added
            {addedOccasion ? ` (${addedOccasion})` : ""}
          </p>
        ) : null}
      </div>
      {alreadyAdded ? (
        <span className="hl-fest-card__badge">Added</span>
      ) : (
        <button
          type="button"
          className="hl-btn hl-btn--sm hl-btn--primary"
          disabled={busy || isAdding}
          onClick={() => onAdd(festival)}
        >
          {isAdding ? "Adding…" : "Add In Holidays"}
        </button>
      )}
    </article>
  );
});

//Created By Cursor
function MdlAllFestivals({
  open,
  festivals,
  holidayByDate,
  busy,
  addingId,
  year,
  onAdd,
  onClose,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const modal = (
    <div
      className="hl-mdl hl-mdl--wide"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hl-fest-all-title"
    >
      <button
        type="button"
        className="hl-mdl__backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="hl-mdl__panel hl-mdl__panel--wide">
        <header className="hl-mdl__hero">
          <div className="hl-mdl__hero-icon" aria-hidden>
            <FaCalendarAlt />
          </div>
          <div className="min-w-0">
            <h2 id="hl-fest-all-title" className="hl-mdl__title">
              All festivals
            </h2>
            <p className="hl-mdl__sub">
              {year} · {festivals.length} festival
              {festivals.length === 1 ? "" : "s"}
            </p>
          </div>
          <button
            type="button"
            className="hl-mdl__close"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <FaTimes />
          </button>
        </header>

        <div className="hl-mdl__scroll">
          {festivals.length === 0 ? (
            <div className="hl-empty hl-empty--compact">
              <p className="hl-empty__title">No festivals found</p>
              <p className="hl-empty__hint">Nothing listed for {year}.</p>
            </div>
          ) : (
            <div className="hl-fest-list">
              {festivals.map((f) => {
                const date = parseYmd(f.festival_date);
                const addedOccasion = date ? holidayByDate.get(date) : "";
                return (
                  <FestivalRow
                    key={f.festival_id}
                    festival={f}
                    alreadyAdded={!!addedOccasion}
                    addedOccasion={addedOccasion || ""}
                    busy={busy}
                    addingId={addingId}
                    onAdd={onAdd}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

//Created By Cursor
//Created By Cursor
function MdlAddHoliday({
  open,
  saving,
  form,
  isEdit,
  onChange,
  onClose,
  onSave,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving && onClose) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, saving, onClose]);

  if (!open) return null;

  const modal = (
    <div
      className="hl-mdl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hl-mdl-title"
    >
      <button
        type="button"
        className="hl-mdl__backdrop"
        aria-label="Close"
        onClick={onClose}
        disabled={saving}
      />
      <div className="hl-mdl__panel">
        <header className="hl-mdl__hero">
          <div className="hl-mdl__hero-icon" aria-hidden>
            {isEdit ? <FaEdit /> : <FaCalendarPlus />}
          </div>
          <div className="min-w-0">
            <h2 id="hl-mdl-title" className="hl-mdl__title">
              {isEdit ? "Edit holiday" : "Add holiday"}
            </h2>
            <p className="hl-mdl__sub">
              {isEdit
                ? "Update holiday details for this session"
                : "Schedule a school holiday for this session"}
            </p>
          </div>
          <button
            type="button"
            className="hl-mdl__close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close dialog"
          >
            <FaTimes />
          </button>
        </header>

        <div className="hl-mdl__body">
          <label className="hl-field">
            <span>Start date</span>
            <input
              type="date"
              value={form.startDate}
              onChange={(e) => onChange("startDate", e.target.value)}
              disabled={saving}
            />
          </label>
          <label className="hl-field">
            <span>End date</span>
            <input
              type="date"
              value={form.endDate}
              onChange={(e) => onChange("endDate", e.target.value)}
              disabled={saving}
            />
          </label>
          <label className="hl-field hl-field--full">
            <span>Occasion</span>
            <input
              type="text"
              value={form.occasion}
              onChange={(e) => onChange("occasion", e.target.value)}
              placeholder="e.g. Diwali, Summer break"
              disabled={saving}
            />
          </label>
          <label className="hl-field hl-field--full">
            <span>Remark</span>
            <input
              type="text"
              value={form.remark}
              onChange={(e) => onChange("remark", e.target.value)}
              placeholder="Optional note"
              disabled={saving}
            />
          </label>
          <label className="hl-field hl-field--full">
            <span>Holiday type</span>
            <select
              value={form.type}
              onChange={(e) => onChange("type", e.target.value)}
              disabled={saving}
            >
              <option value="">Select holiday type</option>
              {HOLIDAY_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="hl-mdl__footer">
          <button
            type="button"
            className="hl-btn hl-btn--ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="hl-btn hl-btn--primary"
            onClick={onSave}
            disabled={saving}
          >
            {saving
              ? isEdit
                ? "Updating…"
                : "Saving…"
              : isEdit
                ? "Update holiday"
                : "Save holiday"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

const EMPTY_FORM = {
  holidayId: "",
  startDate: "",
  endDate: "",
  occasion: "",
  remark: "",
  type: "",
};

//Created By Cursor
export default function Holiday() {
  const [loggedIn] = useState(() => !!localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [holidays, setHolidays] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [festivalsLoading, setFestivalsLoading] = useState(false);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [addingFestivalId, setAddingFestivalId] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  const now = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState(() => now.getFullYear());
  const [viewMonth, setViewMonth] = useState(() => now.getMonth()); // 0-based

  const monthLabel = MONTH_NAMES[viewMonth];
  const isCurrentMonth =
    viewYear === now.getFullYear() && viewMonth === now.getMonth();

  // Created By Cursor — default to current session, allow change via picker
  useEffect(() => {
    try {
      const current = SessionManager.getCurrentSession()?.[0];
      if (current?.session_id) {
        setSessionId(current.session_id);
        setSessionName(current.session_name || "");
      }
    } catch (e) {
      // ignore
    }
    setSessionReady(true);
  }, []);

  const onSessionPicked = useCallback((id, name) => {
    setSessionId(id || "");
    setSessionName(name || "");
    setSessionDialogOpen(false);
    setHolidays([]);
  }, []);

  const goPrevMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const goNextMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, []);

  const goThisMonth = useCallback(() => {
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
  }, [now]);

  // date (YYYY-MM-DD) -> holiday occasion covering that date
  const holidayByDate = useMemo(() => {
    const map = new Map();
    holidays.forEach((h) => {
      const occasion = String(h.holiday_Occasion || "").trim() || "Holiday";
      eachYmdInRange(h.start_holiday_date, h.end_holiday_date, (ymd) => {
        if (!map.has(ymd)) map.set(ymd, occasion);
      });
    });
    return map;
  }, [holidays]);

  const monthFestivals = useMemo(() => {
    return festivals.filter((f) => {
      const d = parseYmd(f.festival_date);
      if (!d || d.length < 7) return false;
      const [y, m] = d.split("-").map(Number);
      return y === viewYear && m === viewMonth + 1;
    });
  }, [festivals, viewYear, viewMonth]);

  const loadFestivals = useCallback(async () => {
    setFestivalsLoading(true);
    try {
      const response = await axios
        .get(`${API_BASE()}getFestivals.php`, {
          params: { year: viewYear },
        })
        .then((res) => res.data);

      if (response?.api_status === "OK" && Array.isArray(response.data)) {
        setFestivals(response.data);
      } else {
        setFestivals([]);
      }
    } catch (error) {
      console.error("Error fetching festivals:", error);
      setFestivals([]);
    } finally {
      setFestivalsLoading(false);
    }
  }, [viewYear]);

  const loadHolidays = useCallback(
    async ({ soft = false } = {}) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode || !sessionId) {
        setHolidays([]);
        setLoading(false);
        if (sessionReady && !sessionId) {
          myToast.notify_warning("Select a session to load holidays.");
        }
        return;
      }

      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const response = await axios
          .get(`${API_BASE()}getHolidays.php`, {
            params: { school_code: schoolCode, session_id: sessionId },
          })
          .then((res) => res.data);

        if (response?.api_status === "OK") {
          setHolidays(Array.isArray(response.holidays) ? response.holidays : []);
        } else {
          setHolidays([]);
          myToast.notify_warning(
            response?.api_remark || "Failed to load holidays."
          );
        }
      } catch (error) {
        console.error("Error fetching holidays:", error);
        setHolidays([]);
        myToast.notify_warning("Failed to load holidays.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [sessionId, sessionReady]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!loggedIn || !sessionReady) return;
    if (!sessionId) {
      setLoading(false);
      setHolidays([]);
      return;
    }
    loadHolidays();
  }, [loggedIn, sessionReady, sessionId, loadHolidays]);

  useEffect(() => {
    if (!loggedIn) return;
    loadFestivals();
  }, [loggedIn, loadFestivals]);

  const openAdd = useCallback(() => {
    if (!sessionId) {
      myToast.notify_warning("Select a session first.");
      setSessionDialogOpen(true);
      return;
    }
    setForm(EMPTY_FORM);
    setAddOpen(true);
  }, [sessionId]);

  const openEdit = useCallback((item) => {
    if (!item?.holiday_id) return;
    setForm({
      holidayId: item.holiday_id,
      startDate: parseYmd(item.start_holiday_date),
      endDate: parseYmd(item.end_holiday_date),
      occasion: item.holiday_Occasion || "",
      remark: item.holiday_remark || "",
      type: item.holiday_type || "",
    });
    setAddOpen(true);
  }, []);

  const closeAdd = useCallback(() => {
    if (saving) return;
    setAddOpen(false);
    setForm(EMPTY_FORM);
  }, [saving]);

  const onFormChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const registerHolidayPayload = useCallback(
    async ({ startDate, endDate, occasion, remark, type }) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode || !sessionId) {
        myToast.notify_warning("Missing school or session.");
        return false;
      }
      if (!startDate || !endDate) {
        myToast.notify_warning("Start date and end date are required.");
        return false;
      }
      if (endDate < startDate) {
        myToast.notify_warning("End date cannot be before start date.");
        return false;
      }
      if (!String(occasion || "").trim()) {
        myToast.notify_warning("Holiday occasion is required.");
        return false;
      }
      if (!String(type || "").trim()) {
        myToast.notify_warning("Holiday type is required.");
        return false;
      }

      const response = await axios
        .get(`${API_BASE()}registerHolidays.php`, {
          params: {
            school_code: schoolCode,
            session_id: sessionId,
            holiday_start_date: startDate,
            holiday_end_date: endDate,
            holiday_occasion: occasion.trim(),
            holiday_remark: (remark || "").trim(),
            holiday_type: type.trim(),
          },
        })
        .then((res) => res.data);

      if (response?.api_status === "OK") {
        return true;
      }
      myToast.notify_warning(
        response?.api_remark || "Failed to register holiday."
      );
      return false;
    },
    [sessionId]
  );

  const updateHolidayPayload = useCallback(
    async ({ holidayId, startDate, endDate, occasion, remark, type }) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode || !sessionId) {
        myToast.notify_warning("Missing school or session.");
        return false;
      }
      if (!holidayId) {
        myToast.notify_warning("Missing holiday id.");
        return false;
      }
      if (!startDate || !endDate) {
        myToast.notify_warning("Start date and end date are required.");
        return false;
      }
      if (endDate < startDate) {
        myToast.notify_warning("End date cannot be before start date.");
        return false;
      }
      if (!String(occasion || "").trim()) {
        myToast.notify_warning("Holiday occasion is required.");
        return false;
      }
      if (!String(type || "").trim()) {
        myToast.notify_warning("Holiday type is required.");
        return false;
      }

      const response = await axios
        .get(`${API_BASE()}updateHolidays.php`, {
          params: {
            school_code: schoolCode,
            session_id: sessionId,
            holiday_id: holidayId,
            holiday_start_date: startDate,
            holiday_end_date: endDate,
            holiday_occasion: occasion.trim(),
            holiday_remark: (remark || "").trim(),
            holiday_type: type.trim(),
          },
        })
        .then((res) => res.data);

      if (response?.api_status === "OK") {
        return true;
      }
      myToast.notify_warning(
        response?.api_remark || "Failed to update holiday."
      );
      return false;
    },
    [sessionId]
  );

  const saveHoliday = useCallback(async () => {
    setSaving(true);
    try {
      const isEdit = !!form.holidayId;
      const ok = isEdit
        ? await updateHolidayPayload(form)
        : await registerHolidayPayload(form);
      if (ok) {
        myToast.notify_success(
          isEdit
            ? "Holiday updated successfully."
            : "Holiday added successfully."
        );
        setAddOpen(false);
        setForm(EMPTY_FORM);
        await loadHolidays({ soft: true });
      }
    } catch (error) {
      console.error("Error saving holiday:", error);
      myToast.notify_warning(
        form.holidayId
          ? "Failed to update holiday."
          : "Failed to register holiday."
      );
    } finally {
      setSaving(false);
    }
  }, [form, loadHolidays, registerHolidayPayload, updateHolidayPayload]);

  const addFestivalToHolidays = useCallback(
    async (festival) => {
      const date = parseYmd(festival.festival_date);
      const occasion = String(festival.festival_name || "").trim();
      if (date && holidayByDate.has(date)) {
        myToast.notify_warning(
          `On this date holiday is added${
            holidayByDate.get(date)
              ? ` (${holidayByDate.get(date)})`
              : ""
          }.`
        );
        return;
      }
      if (!date || !occasion) {
        myToast.notify_warning("Festival is missing date or name.");
        return;
      }

      setAddingFestivalId(festival.festival_id);
      try {
        const ok = await registerHolidayPayload({
          startDate: date,
          endDate: date,
          occasion,
          remark: festival.short_desc || "",
          type: isPublicHoliday(festival) ? "Gazetted" : "Festival",
        });
        if (ok) {
          myToast.notify_success(`"${occasion}" added to holidays.`);
          await loadHolidays({ soft: true });
        }
      } catch (error) {
        console.error("Error adding festival to holidays:", error);
        myToast.notify_warning("Failed to add festival to holidays.");
      } finally {
        setAddingFestivalId("");
      }
    },
    [holidayByDate, loadHolidays, registerHolidayPayload]
  );

  const handleDelete = useCallback(
    async (item) => {
      const holidayId = item?.holiday_id;
      if (!holidayId) return;

      const label = item.holiday_Occasion || holidayId;
      if (
        !window.confirm(`Delete holiday "${label}"? This cannot be undone.`)
      ) {
        return;
      }

      const schoolCode = SessionManager.getSchoolCode();
      const staffId = SessionManager.getStaffDetails()?.staff_id;

      if (!staffId) {
        myToast.notify_warning("Staff ID not found. Please re-login.");
        return;
      }
      if (!schoolCode || !sessionId) {
        myToast.notify_warning("Missing school or session.");
        return;
      }

      setDeletingId(holidayId);
      try {
        const response = await axios
          .get(`${API_BASE()}deleteHolidays.php`, {
            params: {
              school_code: schoolCode,
              session_id: sessionId,
              staff_id: staffId,
              holiday_id: holidayId,
            },
          })
          .then((res) => res.data);

        if (response?.api_status === "OK") {
          myToast.notify_success("Holiday deleted successfully.");
          setHolidays((prev) => prev.filter((h) => h.holiday_id !== holidayId));
        } else {
          myToast.notify_warning(
            response?.api_remark || "Failed to delete holiday."
          );
        }
      } catch (error) {
        console.error("Error deleting holiday:", error);
        myToast.notify_warning("Failed to delete holiday. Please try again.");
      } finally {
        setDeletingId("");
      }
    },
    [sessionId]
  );

  const busy =
    loading ||
    refreshing ||
    saving ||
    !!deletingId ||
    !!addingFestivalId ||
    festivalsLoading;

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page hl-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Holiday List</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell hl-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaCalendarAlt />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Holidays</h1>
              <p className="sm-hero__sub">
                {sessionName
                  ? `${sessionName} · ${holidays.length} holiday${
                      holidays.length === 1 ? "" : "s"
                    }`
                  : "Select a session to manage holidays"}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className="hl-session"
              onClick={() => setSessionDialogOpen(true)}
              disabled={busy}
              title="Change session"
            >
              <span className="hl-session__icon" aria-hidden>
                <FaCalendarAlt />
              </span>
              <span className="hl-session__text">
                <small>Session</small>
                <strong>{sessionName || "Select session"}</strong>
              </span>
              <span className="hl-session__hint">Change</span>
            </button>
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              onClick={() => {
                loadHolidays({ soft: true });
                loadFestivals();
              }}
              disabled={busy || !sessionId}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <button
              type="button"
              className="hl-cta"
              onClick={openAdd}
              disabled={busy || !sessionId}
            >
              <FaPlus aria-hidden />
              <span>Add holiday</span>
            </button>
          </div>
        </header>

        <div className="hl-body">
          <section
            className="hl-fest-section"
            aria-label={`${monthLabel} ${viewYear} festivals`}
          >
            <div className="hl-fest-section__head">
              <div className="min-w-0">
                <h2 className="hl-fest-section__title">
                  Festivals · {monthLabel} {viewYear}
                </h2>
                <p className="hl-fest-section__sub">
                  {festivalsLoading
                    ? "Loading festivals…"
                    : `${monthFestivals.length} in ${monthLabel} · ${festivals.length} in ${viewYear}`}
                </p>
              </div>
              <div className="hl-month-nav">
                <button
                  type="button"
                  className="hl-btn hl-btn--ghost hl-btn--sm"
                  onClick={goPrevMonth}
                  disabled={festivalsLoading}
                  title="Previous month"
                >
                  <FaChevronLeft aria-hidden /> Prev
                </button>
                {!isCurrentMonth ? (
                  <button
                    type="button"
                    className="hl-btn hl-btn--ghost hl-btn--sm"
                    onClick={goThisMonth}
                    disabled={festivalsLoading}
                  >
                    This Month
                  </button>
                ) : null}
                <button
                  type="button"
                  className="hl-btn hl-btn--ghost hl-btn--sm"
                  onClick={goNextMonth}
                  disabled={festivalsLoading}
                  title="Next month"
                >
                  Next <FaChevronRight aria-hidden />
                </button>
                <button
                  type="button"
                  className="hl-btn hl-btn--ghost hl-btn--sm"
                  onClick={() => setViewAllOpen(true)}
                  disabled={festivalsLoading}
                >
                  View All
                </button>
              </div>
            </div>

            {festivalsLoading ? (
              <div className="hl-fest-loading">
                <MyLoader />
              </div>
            ) : monthFestivals.length === 0 ? (
              <div className="hl-fest-empty">
                No festivals in {monthLabel} {viewYear}. Use Next / Prev or View
                All.
              </div>
            ) : (
              <div className="hl-fest-list">
                {monthFestivals.map((f) => {
                  const date = parseYmd(f.festival_date);
                  const addedOccasion = date ? holidayByDate.get(date) : "";
                  return (
                    <FestivalRow
                      key={f.festival_id}
                      festival={f}
                      alreadyAdded={!!addedOccasion}
                      addedOccasion={addedOccasion || ""}
                      busy={busy}
                      addingId={addingFestivalId}
                      onAdd={addFestivalToHolidays}
                    />
                  );
                })}
              </div>
            )}
          </section>

          <div className="hl-holidays-head">
            <h2 className="hl-holidays-head__title">Holiday list</h2>
            <span className="hl-holidays-head__count">
              {holidays.length} item{holidays.length === 1 ? "" : "s"}
            </span>
          </div>

          {loading ? (
            <div className="hl-loading">
              <MyLoader />
            </div>
          ) : holidays.length === 0 ? (
            <div className="hl-empty">
              <div className="hl-empty__icon" aria-hidden>
                <FaCalendarAlt />
              </div>
              <p className="hl-empty__title">No holidays yet</p>
              <p className="hl-empty__hint">
                Add from festivals above, or create a custom holiday.
              </p>
              <button
                type="button"
                className="hl-btn hl-btn--primary"
                onClick={openAdd}
              >
                <FaPlus aria-hidden /> Add holiday
              </button>
            </div>
          ) : (
            <div className="hl-list">
              {holidays.map((item, index) => (
                <HolidayCard
                  key={item.holiday_id || `${item.holiday_Occasion}-${index}`}
                  item={item}
                  index={index}
                  busy={busy}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <MdlAddHoliday
        open={addOpen}
        saving={saving}
        form={form}
        isEdit={!!form.holidayId}
        onChange={onFormChange}
        onClose={closeAdd}
        onSave={saveHoliday}
      />

      <MdlAllFestivals
        open={viewAllOpen}
        festivals={festivals}
        holidayByDate={holidayByDate}
        busy={busy}
        addingId={addingFestivalId}
        year={viewYear}
        onAdd={addFestivalToHolidays}
        onClose={() => setViewAllOpen(false)}
      />

      {sessionDialogOpen ? (
        <MdlSelectSession
          action={() => setSessionDialogOpen(false)}
          action2={onSessionPicked}
        />
      ) : null}
    </div>
  );
}
